<template>
  <section ref="root" class="ht-flip" :aria-label="$t('ServiceCarousel.region')">
    <carousel
      ref="carousel"
      :per-page="1"
      :per-page-custom="perPageCustom"
      :center-mode="true"
      :speed="500"
      :mouse-drag="true"
      :navigation-enabled="true"
      :navigation-click-target-size="0"
      :navigation-next-label="nextLabel"
      :navigation-prev-label="prevLabel"
      :pagination-size="perPage === 1 ? 6 : 8"
      :pagination-padding="perPage === 1 ? 4 : 6"
      :pagination-color="userLogin ? 'rgba(36, 23, 47, 0.18)' : 'rgba(245, 242, 250, 0.22)'"
      :pagination-active-color="userLogin ? '#9228c8' : '#c98bfa'"
      @mounted="onCarouselMounted"
      @page-change="onPageChange"
    >
      <slide
        v-for="(item, index) in items"
        :key="index"
        class="ht-flip__slide"
        :title="$t('ServiceCarousel.page', { n: index + 1 })"
      >
        <article
          class="ht-flip-card"
          :class="{ 'is-flipped': flippedIndex === index }"
          :tabindex="isVisible(index) ? 0 : -1"
          :aria-labelledby="`ht-flip-title-${index}`"
          @pointerdown="onPointerDown"
          @click="onCardClick(index, $event)"
        >
          <div class="ht-flip-card__inner">
            <div class="ht-flip-card__face ht-flip-card__front" aria-hidden="true">
              <span class="ht-flip-card__index">{{ number(index) }}</span>
              <div class="ht-flip-card__media">
                <img :src="item.image" alt="" draggable="false" />
              </div>
              <h4 class="ht-flip-card__title">{{ item.title }}</h4>
            </div>
            <div class="ht-flip-card__face ht-flip-card__back">
              <span class="ht-flip-card__index" aria-hidden="true">{{ number(index) }}</span>
              <h4 :id="`ht-flip-title-${index}`" class="ht-flip-card__title">{{ item.title }}</h4>
              <span class="ht-flip-card__rule" aria-hidden="true"></span>
              <p class="ht-flip-card__desc">{{ item.description }}</p>
            </div>
          </div>
        </article>
      </slide>
    </carousel>
  </section>
</template>

<script>
const images = [
  require('@/assets/images/Newlandindpage/lightmode/1.png'),
  require('@/assets/images/Newlandindpage/lightmode/2.png'),
  require('@/assets/images/Newlandindpage/lightmode/3.png'),
  require('@/assets/images/Newlandindpage/lightmode/g1.png'),
  require('@/assets/images/Newlandindpage/lightmode/555.png'),
  require('@/assets/images/Newlandindpage/lightmode/666.png'),
  require('@/assets/images/Newlandindpage/lightmode/7.png'),
  require('@/assets/images/Newlandindpage/lightmode/8.png'),
  require('@/assets/images/Newlandindpage/lightmode/3.png'),
  require('@/assets/images/Newlandindpage/lightmode/10.png'),
  require('@/assets/images/Newlandindpage/lightmode/11.png'),
  require('@/assets/images/Newlandindpage/lightmode/12.png'),
  require('@/assets/images/Newlandindpage/lightmode/13.png')
]

// A tap that moves further than this is a swipe, not a flip.
const TAP_TOLERANCE = 10

export default {
  name: 'ServiceFlipCarousel',
  data() {
    return {
      // vue-carousel matches the widest breakpoint that is <= the viewport width
      perPageCustom: [
        [768, 2],
        [1200, 3]
      ],
      currentPage: 0,
      perPage: 1,
      flippedIndex: null,
      canHover: true,
      pointerStart: null,
      nextLabel: "<span class='ht-flip__chevron ht-flip__chevron--next' aria-hidden='true'></span>",
      prevLabel: "<span class='ht-flip__chevron ht-flip__chevron--prev' aria-hidden='true'></span>"
    }
  },
  computed: {
    userLogin() {
      return this.$store.state.userLogin
    },
    items() {
      return this.$t('SectionImplementation').map((item, index) => ({
        ...item,
        image: images[index]
      }))
    }
  },
  watch: {
    '$i18n.locale'() {
      this.$nextTick(this.localizeNavigation)
    }
  },
  mounted() {
    this.hoverQuery = window.matchMedia('(hover: hover)')
    this.canHover = this.hoverQuery.matches
    window.addEventListener('resize', this.syncPerPage)
    this.syncPerPage()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.syncPerPage)
  },
  methods: {
    number(index) {
      return String(index + 1).padStart(2, '0')
    },
    isVisible(index) {
      // vue-carousel clamps the last page so it still shows a full row
      const start = Math.min(this.currentPage * this.perPage, this.items.length - this.perPage)
      return index >= start && index < start + this.perPage
    },
    syncPerPage() {
      const carousel = this.$refs.carousel
      if (carousel) this.perPage = carousel.breakpointSlidesPerPage
      this.canHover = this.hoverQuery ? this.hoverQuery.matches : true
    },
    onCarouselMounted() {
      // The arrows render only after the slides register, one tick later.
      this.$nextTick(() => {
        this.syncPerPage()
        this.localizeNavigation()
      })
    },
    onPageChange(page) {
      this.currentPage = page
      this.flippedIndex = null
      this.syncPerPage()
      this.localizeNavigation()
    },
    onPointerDown(event) {
      this.pointerStart = { x: event.clientX, y: event.clientY }
    },
    onCardClick(index, event) {
      // Pointer devices flip on hover; taps toggle only where hover is unavailable.
      if (this.canHover) return
      const start = this.pointerStart
      this.pointerStart = null
      if (start) {
        const moved = Math.hypot(event.clientX - start.x, event.clientY - start.y)
        if (moved > TAP_TOLERANCE) return
      }
      this.flippedIndex = this.flippedIndex === index ? null : index
    },
    // vue-carousel hard-codes English labels on its arrow buttons.
    localizeNavigation() {
      const root = this.$refs.root
      if (!root) return
      const prev = root.querySelector('.VueCarousel-navigation-prev')
      const next = root.querySelector('.VueCarousel-navigation-next')
      if (prev) prev.setAttribute('aria-label', this.$t('ServiceCarousel.prev'))
      if (next) next.setAttribute('aria-label', this.$t('ServiceCarousel.next'))
    }
  }
}
</script>
